// Copyright (c) 2018-2022 Coinbase, Inc. <https://www.coinbase.com/>
// Licensed under the Apache License, version 2.0
// This file is derived from coinbase-wallet-sdk/packages/wallet-sdk/src/provider/CoinbaseWalletProvider.ts (2022/08/01).
// Modified for the klip-web3-provider development.

import { BigNumber, ethers } from 'ethers';

import SafeEventEmitter from '@metamask/safe-event-emitter';
import { convertHexToUtf8 } from '@walletconnect/utils';
import { ethErrors } from 'eth-rpc-errors';
import Caver from 'caver-js';
import QRCodeModal from '../../libs/klip-qrcode-modal';
import { JSONRPCResponse, JSONRPCRequest, JSONRPCMethod } from './JSONRPC';
import { Web3Provider, RequestArguments } from './Web3Provider';
import { SubscriptionManager, SubscriptionNotification, SubscriptionResult } from './SubscriptionManager';
import {
    delegateRaw,
    estimate,
    getEncryptPlain,
    getGasData,
    getNetworkNonce,
    sign,
    signTypedData,
} from './src/abc/transactions';
import { createSecureChannel } from './src/abc/securechannel';
import secureLocalStorage from 'react-secure-storage';

// TODO : 별도의 npm 패키지로 분리
import { tokenLogin } from './src/abc/login';
import { recoveryWallet } from './src/abc/mpc';
import { getFeePayerSignatureHex } from './src/utils/feePayer';
import { getSenderSignatureHex } from './src/utils/sender';
import { calculateGasMargin } from './src/utils/calculateGasMargin';
import { sleep } from './src/utils/sleep';

// const CypressChainId = '0x2019';
// const CypressChainId = '0x3e9';
const ErrorMsgCaverUndefined = 'RPC Url is not provided or chain id is different from Klaytn Mainnet.';
const ErrorMsgProviderUndefined = 'RPC Url is not provided or chain id is different.';
const KlipUrl = 'https://klipwallet.com/?target=/a2a?request_key=';
export type Callback<T> = (err: Error | null, result: T | null) => void;

export interface IAbcProviderOptions {
    bappName?: string;
    rpcUrl?: string;
    chainId?: string;
    accessKeyId?: string;
    secretAccessKey?: string;
    email?: string;
    id_token?: string;
    serv?: string;
}

interface EstimateGasParams {
    to?: string;
    from?: string;
    data?: string;
    value?: string;
    gas?: string;
    gasPrice?: string;
}
export interface IQRCodeModalOptions {
    registryUrl?: string;
    mobileLinks?: string[];
    desktopLinks?: string[];
}

interface FeeData {
    gasPrice: BigNumber;
    lastBaseFeePerGas: BigNumber;
    maxFeePerGas: BigNumber;
    maxPriorityFeePerGas: BigNumber;
}

export interface AbcWalletResult {
    encryptDevicePassword: string;
    ourpubkey: string;
    pvencstr: string;
    sid: string;
    ucpubkey: string;
    uid: string;
    wid: number;
}

export interface Eip1559Result {
    low: {
        suggestedMaxPriorityFeePerGas: string;
        suggestedMaxFeePerGas: string;
        minWaitTimeEstimate: number;
        maxWaitTimeEstimate: number;
    };
    medium: {
        suggestedMaxPriorityFeePerGas: string;
        suggestedMaxFeePerGas: string;
        minWaitTimeEstimate: number;
        maxWaitTimeEstimate: number;
    };
    high: {
        suggestedMaxPriorityFeePerGas: string;
        suggestedMaxFeePerGas: string;
        minWaitTimeEstimate: number;
        maxWaitTimeEstimate: number;
    };
    estimatedBaseFee: string;
    networkCongestion: string;
    latestPriorityFeeRange: string[];
    historicalPriorityFeeRange: string[];
    historicalBaseFeeRange: string[];
    priorityFeeTrend: string;
    baseFeeTrend: string;
}

export class AbcWeb3Provider extends SafeEventEmitter implements Web3Provider {
    public qrcode = true;
    public chainId = '';
    public qrcodeModal = QRCodeModal;
    public qrcodeModalOptions: IQRCodeModalOptions | undefined = undefined;
    public bappName = '';
    public rpcUrl = '';
    public caver: any;
    public caverExt: any;
    public caverEnabled = false;
    public provider: any;
    public providerEnabled = false;
    private readonly _subscriptionManager = new SubscriptionManager(this);
    private _addresses: string[] = [];
    public email = '';
    public id_token = '';
    public serv = '';

    constructor(opts: IAbcProviderOptions) {
        super({});
        this.bappName = opts.bappName ? opts.bappName : 'undefined';
        this.rpcUrl = opts.rpcUrl ? opts.rpcUrl : 'undefined';
        // console.log('!! AbcWeb3Provider RPCURL = ', this.rpcUrl);
        this.chainId = opts.chainId ? opts.chainId : '0x1'; // Default = Ethereum Mainnet
        this.caver = this.rpcUrl != 'undefined' ? new Caver(new Caver.providers.HttpProvider(this.rpcUrl)) : undefined;
        this.provider = this.rpcUrl != 'undefined' ? ethers.getDefaultProvider(this.rpcUrl) : undefined;
        this._subscriptionManager.events.on('notification', (notification: SubscriptionNotification) => {
            this.emit('message', {
                type: notification.method,
                data: notification.params,
            });
        });
        this.email = opts.email ?? 'undefined';
        this.id_token = opts.id_token ?? 'undefined';
        this.serv = opts.serv ?? 'undefined';
    }

    private async _checkRpcUrl(): Promise<boolean> {
        const chainId = await this.caver.rpc.klay.getChainId();
        // return chainId === CypressChainId;
        return chainId === this.chainId;
    }
    private async _checkRpcUrlNew(): Promise<boolean> {
        const network = await this.provider.getNetwork();
        console.log('!! _checkRpcUrlNew : ', network);
        return network.chainId === parseInt(this.chainId, 16);
    }

    public getChainId(): string {
        return this.chainId;
    }

    public get connected(): boolean {
        return true;
    }

    public supportsSubscriptions(): boolean {
        return false;
    }

    public disconnect(): boolean {
        return true;
    }

    public send(request: JSONRPCRequest): JSONRPCResponse;
    public send(request: JSONRPCRequest[]): JSONRPCResponse[];
    public send(request: JSONRPCRequest, callback: Callback<JSONRPCResponse>): void;
    public send(request: JSONRPCRequest[], callback: Callback<JSONRPCResponse[]>): void;
    public send<T = any>(method: string, params?: any[] | any): Promise<T>;
    public send(
        requestOrMethod: JSONRPCRequest | JSONRPCRequest[] | string,
        callbackOrParams?: Callback<JSONRPCResponse> | Callback<JSONRPCResponse[]> | any[] | any,
    ): JSONRPCResponse | JSONRPCResponse[] | void | Promise<any> {
        // send<T>(method, params): Promise<T>
        if (typeof requestOrMethod === 'string') {
            const method = requestOrMethod;
            const params = Array.isArray(callbackOrParams)
                ? callbackOrParams
                : callbackOrParams !== undefined
                ? [callbackOrParams]
                : [];
            const request: JSONRPCRequest = {
                jsonrpc: '2.0',
                id: 0,
                method,
                params,
            };
            return this._sendRequestAsync(request).then((res) => res.result);
        }

        // send(JSONRPCRequest | JSONRPCRequest[], callback): void
        if (typeof callbackOrParams === 'function') {
            const request = requestOrMethod as any;
            const callback = callbackOrParams;
            return this._sendAsync(request, callback);
        }

        // send(JSONRPCRequest[]): JSONRPCResponse[]
        if (Array.isArray(requestOrMethod)) {
            const requests = requestOrMethod;
            return requests.map((r) => this._sendRequest(r));
        }

        // send(JSONRPCRequest): JSONRPCResponse
        const req: JSONRPCRequest = requestOrMethod;
        return this._sendRequest(req);
    }

    public sendAsync(request: JSONRPCRequest, callback: Callback<JSONRPCResponse>): void;
    public sendAsync(request: JSONRPCRequest[], callback: Callback<JSONRPCResponse[]>): void;

    public async sendAsync(
        request: JSONRPCRequest | JSONRPCRequest[],
        callback: Callback<JSONRPCResponse> | Callback<JSONRPCResponse[]>,
    ): Promise<void> {
        if (typeof callback !== 'function') {
            throw new Error('callback is required');
        }

        // send(JSONRPCRequest[], callback): void
        if (Array.isArray(request)) {
            const arrayCb = callback as Callback<JSONRPCResponse[]>;
            this._sendMultipleRequestsAsync(request)
                .then((responses) => arrayCb(null, responses))
                .catch((err) => arrayCb(err, null));
            return;
        }

        // send(JSONRPCRequest, callback): void
        const cb = callback as Callback<JSONRPCResponse>;
        return this._sendRequestAsync(request)
            .then((response) => cb(null, response))
            .catch((err) => cb(err, null));
    }

    public async request<T>(args: RequestArguments): Promise<T> {
        if (!args || typeof args !== 'object' || Array.isArray(args)) {
            throw ethErrors.rpc.invalidRequest({
                message: 'Expected a single, non-array, object argument.',
                data: args,
            });
        }

        const { method, params } = args;

        if (typeof method !== 'string' || method.length === 0) {
            throw ethErrors.rpc.invalidRequest({
                message: "'args.method' must be a non-empty string.",
                data: args,
            });
        }

        if (params !== undefined && !Array.isArray(params) && (typeof params !== 'object' || params === null)) {
            throw ethErrors.rpc.invalidRequest({
                message: "'args.params' must be an object or array if provided.",
                data: args,
            });
        }

        const newParams = params === undefined ? [] : params;

        const res = await this._sendRequestAsync({
            method,
            params: newParams,
            jsonrpc: '2.0',
            id: 0,
        });
        return res.result as T;
    }

    private _send = this.send.bind(this);
    private _sendAsync = this.sendAsync.bind(this);

    private _sendRequest(request: JSONRPCRequest): JSONRPCResponse {
        const response: JSONRPCResponse = {
            jsonrpc: '2.0',
            id: request.id,
        };
        const { method } = request;

        response.result = this._handleSynchronousMethods(request);

        if (response.result === undefined) {
            throw new Error(
                `Kaikas Wallet does not support calling ${method} synchronously without ` +
                    `a callback. Please provide a callback parameter to call ${method} ` +
                    `asynchronously.`,
            );
        }
        return response;
    }

    private _sendRequestAsync(request: JSONRPCRequest): Promise<JSONRPCResponse> {
        return new Promise<JSONRPCResponse>((resolve, reject) => {
            console.log('!! _sendRequestAsync : ', request.method);
            try {
                const syncResult = this._handleSynchronousMethods(request);
                if (syncResult !== undefined) {
                    return resolve({
                        jsonrpc: '2.0',
                        id: request.id,
                        result: syncResult,
                    });
                }

                const subscriptionPromise = this._handleSubscriptionMethods(request);
                if (subscriptionPromise !== undefined) {
                    subscriptionPromise
                        .then((res) =>
                            resolve({
                                jsonrpc: '2.0',
                                id: request.id,
                                result: res.result,
                            }),
                        )
                        .catch((err) => reject(err));
                    return;
                }
            } catch (err: any) {
                return reject(err);
            }

            this._handleAsynchronousMethods(request)
                .then((res) => res && resolve({ ...res, id: request.id }))
                .catch((err) => reject(err));
        });
    }

    private _handleSubscriptionMethods(request: JSONRPCRequest): Promise<SubscriptionResult> | undefined {
        switch (request.method) {
            case JSONRPCMethod.eth_subscribe:
            case JSONRPCMethod.eth_unsubscribe:
                return this._subscriptionManager.handleRequest(request);
        }

        return undefined;
    }
    private _sendMultipleRequestsAsync(requests: JSONRPCRequest[]): Promise<JSONRPCResponse[]> {
        return Promise.all(requests.map((r) => this._sendRequestAsync(r)));
    }

    private _handleSynchronousMethods(request: JSONRPCRequest) {
        const { method } = request;

        switch (method) {
            case JSONRPCMethod.eth_accounts:
                return this._eth_accounts();

            case JSONRPCMethod.net_version:
                return this._net_version();

            case JSONRPCMethod.eth_chainId:
                return this._eth_chainId();

            default:
                return undefined;
        }
    }

    private async _handleAsynchronousMethods(request: JSONRPCRequest): Promise<JSONRPCResponse | void> {
        const { method } = request;
        const params = request.params || [];
        console.log('!!!! AbcWeb3Provider : async method = ', method);
        switch (method) {
            case JSONRPCMethod.eth_getBalance:
                return this._eth_getBalance(params);
            case JSONRPCMethod.personal_sign:
                return this._personal_sign(params);
            case JSONRPCMethod.eth_sendTransaction:
                return (this.chainId === '0x2019' || this.chainId === '0x3e9') &&
                    process.env.REACT_APP_KAIA_FEE_DELEGATION === 'true'
                    ? this._eth_sendTransaction_delegated(params)
                    : this._eth_sendTransaction(params);
            case JSONRPCMethod.personal_ecRecover:
                return this._personal_ecRecover(params);
            case JSONRPCMethod.eth_blockNumber:
                return this._eth_blockNumber();
            case JSONRPCMethod.eth_getBlockByNumber:
                return this._eth_getBlockByNumber(params);
            case JSONRPCMethod.eth_getGasPrice:
                return this._eth_getGasPrice();
            case JSONRPCMethod.eth_getTransactionByHash:
                return this._eth_getTransactionByHash(params);
            case JSONRPCMethod.eth_getTransactionReceipt:
                return this._eth_getTransactionReceipt(params);
            case JSONRPCMethod.eth_call:
                return this._eth_call(params);
            case JSONRPCMethod.eth_estimateGas:
                return this._eth_estimateGas(params);
            case JSONRPCMethod.eth_signTypedData_v4:
                return this._eth_signTypedData_v4(params);
        }
        throw new Error(`${method} is not supported in klip-web3-provider.`);
    }

    get isWalletConnect() {
        return true;
    }

    enable = async (): Promise<string[] | undefined> => {
        if (this.caver != undefined) {
            this.caverEnabled = await this._checkRpcUrl();
        }
        if (this.provider != undefined) {
            this.providerEnabled = await this._checkRpcUrlNew();
        }
        if (this._addresses.length > 0) {
            return this._addresses;
        }

        const user = JSON.parse(window.sessionStorage.getItem('abcUser') || '{}');
        if (user.accounts.length > 0) {
            this._addresses = [user.accounts[0].ethAddress];
            return this._addresses;
        }
        // TODO : 별도의 npm 패키지로 분리
        // else {
        //     return new Promise(async (resolve, reject) => {
        //         const isExist = await tokenLogin(this.id_token, this.serv);
        //
        //         if (isExist.accessToken) {
        //             const pinCode = JSON.parse((secureLocalStorage.getItem('pinCode') as string) || '{}');
        //             if (pinCode.pincode === '' || pinCode.email !== this.email) {
        //                 QRCodeModal.open(
        //                     'DEFAULT',
        //                     async (pin1: string) => {
        //                         console.log('### pin : ', pin1);
        //                         if (pin1 !== '') {
        //                             QRCodeModal.open(
        //                                 'DEFAULT',
        //                                 async (pin2: string) => {
        //                                     console.log('### pin : ', pin2);
        //                                     if (pin2 !== '') {
        //                                         if (pin1 === pin2) {
        //                                             const result = recoveryWallet(
        //                                                 this.id_token,
        //                                                 this.serv,
        //                                                 this.email,
        //                                                 pin1,
        //                                             );
        //                                             if (result) {
        //                                                 const user = JSON.parse(
        //                                                     window.sessionStorage.getItem('abcUser') || '{}',
        //                                                 );
        //                                                 if (user.accounts.length !== 0) {
        //                                                     this._addresses = [user.accounts[0].ethAddress];
        //                                                     return resolve([user.accounts[0].ethAddress]);
        //                                                 } else {
        //                                                     this._addresses = [];
        //                                                     return reject(
        //                                                         new Error('Process is canceled or error occurs'),
        //                                                     );
        //                                                 }
        //                                             }
        //                                         } else {
        //                                             this._addresses = [];
        //                                             return reject(new Error('PIN mismatched'));
        //                                         }
        //                                     } else {
        //                                         this._addresses = [];
        //                                         return reject(new Error('Process is canceled or error occurs'));
        //                                     }
        //                                 },
        //                                 undefined,
        //                                 'Confirm',
        //                             );
        //                         } else {
        //                             this._addresses = [];
        //                             return reject(new Error('Process is canceled or error occurs'));
        //                         }
        //                     },
        //                     undefined,
        //                     'Next',
        //                 );
        //             } else {
        //                 const result = recoveryWallet(this.id_token, this.serv, this.email, pinCode.pincode);
        //                 if (result) {
        //                     const user = JSON.parse(window.sessionStorage.getItem('abcUser') || '{}');
        //                     if (user.accounts.length !== 0) {
        //                         this._addresses = [user.accounts[0].ethAddress];
        //                         return resolve([user.accounts[0].ethAddress]);
        //                     } else {
        //                         this._addresses = [];
        //                         return reject(new Error('Process is canceled or error occurs'));
        //                     }
        //                 }
        //             }
        //         } else {
        //             this._addresses = [];
        //             return reject(new Error('Process is canceled or error occurs'));
        //         }
        //     });
        // }
    };

    private _eth_accounts(): string[] {
        return [...this._addresses];
    }

    private _net_version(): number {
        return 8217;
    }

    private _eth_chainId(): string {
        return this.getChainId();
    }

    private async _eth_estimateGas(params: unknown[]): Promise<JSONRPCResponse> {
        return new Promise<JSONRPCResponse>(async (resolve, reject) => {
            console.log('!! _eth_estimateGas params ', params);
            const wallet: AbcWalletResult = JSON.parse(window.sessionStorage.getItem('abcWallet') || '{}');

            try {
                let txParams: EstimateGasParams = params[0] as EstimateGasParams;
                const { to, value, data, from } = txParams;
                const gasLimit = await estimate(
                    {
                        network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                        from: from ?? wallet.sid,
                        to: to ?? '0x',
                        value: value,
                        data: data,
                    },
                    'EIP1559',
                );
                console.log('!! eth_estimateGas : ', gasLimit);

                return resolve({
                    jsonrpc: '2.0',
                    id: 0,
                    result: gasLimit,
                    // result: '0x1312D00', // 20,000,000
                });
            } catch (err) {
                return reject(new Error('Process is canceled or error occurs'));
            }
        });
    }

    private async _personal_sign(params: unknown[]): Promise<JSONRPCResponse> {
        return new Promise<JSONRPCResponse>(async (resolve, reject) => {
            console.log('!! _personal_sign params : ', params);
            const value = typeof params[0] === 'string' ? params[0] : 'undefined'; //message
            const from = params[1];
            const abcUser = JSON.parse(<string>window.sessionStorage.getItem('abcUser'));
            console.log('!! _eth_signTypedData_v4 signing : ', value, abcUser.accounts[0]);

            const wallet: AbcWalletResult = JSON.parse(window.sessionStorage.getItem('abcWallet') || '{}');

            // TODO : secure channel can be expired. So refresh it before each tx.
            await createSecureChannel();

            const secureChannel = JSON.parse(window.sessionStorage.getItem('secureChannel') || '{}');
            console.log('!!! secure channel : ', secureChannel);

            const encryptDevicePassword = await getEncryptPlain(wallet.encryptDevicePassword);
            const pvencstr = await getEncryptPlain(wallet.pvencstr);
            const wid = await getEncryptPlain(wallet.wid.toString());
            console.log('### encryptDevicePassword : ', encryptDevicePassword);
            console.log('### pvencstr : ', pvencstr.length);
            console.log('### wid : ', wid);

            try {
                const signature = await sign(
                    {
                        network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                        encryptDevicePassword,
                        pvencstr,
                        uid: wallet.uid,
                        wid,
                        sid: wallet.sid,
                        message: convertHexToUtf8(value),
                        type: 'PERSONAL',
                    },
                    secureChannel.ChannelID,
                );
                console.log('!! _eth_signTypedData_v4 signature : ', signature);

                return resolve({
                    jsonrpc: '2.0',
                    id: 0,
                    result: signature,
                });
            } catch (err) {
                return reject(new Error('sign typed data error'));
            }
        });
    }

    private async _eth_signTypedData_v4(params: any[]): Promise<JSONRPCResponse> {
        // send token transaction & send klay transaction
        return new Promise<JSONRPCResponse>(async (resolve, reject) => {
            console.log('!! _eth_signTypedData_v4 params : ', params);
            const from = params[0];
            const data = typeof params[1] === 'string' ? params[1] : 'undefined'; //Typed Data

            const abcUser = JSON.parse(<string>window.sessionStorage.getItem('abcUser'));
            console.log('!! _eth_signTypedData_v4 signing : ', data, abcUser.accounts[0]);

            const wallet: AbcWalletResult = JSON.parse(window.sessionStorage.getItem('abcWallet') || '{}');

            // TODO : secure channel can be expired. So refresh it before each tx.
            await createSecureChannel();

            const secureChannel = JSON.parse(window.sessionStorage.getItem('secureChannel') || '{}');
            console.log('!!! secure channel : ', secureChannel);

            const encryptDevicePassword = await getEncryptPlain(wallet.encryptDevicePassword);
            const pvencstr = await getEncryptPlain(wallet.pvencstr);
            const wid = await getEncryptPlain(wallet.wid.toString());
            console.log('### encryptDevicePassword : ', encryptDevicePassword);
            console.log('### pvencstr : ', pvencstr.length);
            console.log('### wid : ', wid);

            try {
                const signature = await signTypedData(
                    {
                        network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                        encryptDevicePassword,
                        pvencstr,
                        uid: wallet.uid,
                        wid,
                        sid: wallet.sid,
                        messageJson: data,
                    },
                    secureChannel.ChannelID,
                );
                console.log('!! _eth_signTypedData_v4 signature : ', signature);

                return resolve({
                    jsonrpc: '2.0',
                    id: 0,
                    result: signature,
                });
            } catch (err) {
                return reject(new Error('sign typed data error'));
            }
        });
    }

    // TODO : 수신한 params 로는 일반 Tx와 Delegated Tx를 구분할 수 있는 방법은 ?
    private async _eth_sendTransaction(params: any[]): Promise<JSONRPCResponse> {
        // send token transaction & send klay transaction
        return new Promise<JSONRPCResponse>(async (resolve, reject) => {
            console.log('!! _eth_sendTransaction params : ', params);
            QRCodeModal.open(
                'DEFAULT',
                async (pin: string) => {
                    console.log('### pin : ', pin);
                    if (pin !== '') {
                        const pinCode = JSON.parse((secureLocalStorage.getItem('pinCode') as string) || '{}');
                        if (pinCode.pincode === pin) {
                            let txParams = params[0];
                            const { chainId, nonce, to, value, type, data, from, gasLimit, gas } = txParams;
                            console.log('txParams:::', txParams);

                            const wallet: AbcWalletResult = JSON.parse(
                                window.sessionStorage.getItem('abcWallet') || '{}',
                            );

                            // TODO : secure channel can be expired. So refresh it before each tx.
                            await createSecureChannel();

                            const secureChannel = JSON.parse(window.sessionStorage.getItem('secureChannel') || '{}');
                            console.log('!!! secure channel : ', secureChannel);

                            const encryptDevicePassword = await getEncryptPlain(wallet.encryptDevicePassword);
                            const pvencstr = await getEncryptPlain(wallet.pvencstr);
                            const wid = await getEncryptPlain(wallet.wid.toString());
                            console.log('### encryptDevicePassword : ', encryptDevicePassword);
                            console.log('### pvencstr : ', pvencstr.length);
                            console.log('### wid : ', wid);

                            const gasData: Eip1559Result = await getGasData(
                                this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                                'EIP1559',
                            );
                            console.log('### gasData : ', this.chainId, gasData);

                            const estimated = await estimate(
                                {
                                    network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                                    from: wallet.sid,
                                    to: to ?? '0x',
                                    value: value,
                                    data: data,
                                },
                                'EIP1559',
                            );
                            console.log('### estimate result : ', estimated);

                            let serializedTx = '';
                            try {
                                serializedTx = await sign(
                                    {
                                        network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                                        from: wallet.sid,
                                        to: to,
                                        gasLimit: gasLimit ?? estimated,
                                        value: params[0].value ?? '0x0',
                                        data: params[0].data,
                                        maxPriorityFeePerGas: ethers.utils
                                            .parseUnits(gasData.high.suggestedMaxPriorityFeePerGas, 'gwei')
                                            .toHexString(),
                                        maxFeePerGas: ethers.utils
                                            .parseUnits(gasData.high.suggestedMaxFeePerGas, 'gwei')
                                            .toHexString(),
                                        type: 'EIP1559',
                                        // type: 'LEGACY',
                                        encryptDevicePassword,
                                        pvencstr,
                                        uid: wallet.uid,
                                        wid,
                                        sid: wallet.sid,
                                    },
                                    secureChannel.ChannelID,
                                );
                                console.log('### sign result ### : ', serializedTx);
                            } catch (e) {
                                console.log('### sign error ### : ', e);
                            }

                            try {
                                const txResponse = await this.caver.rpc.klay.sendRawTransaction(serializedTx);
                                console.log('!! txHash = ', txResponse.transactionHash);
                                const receipt = await this.caver.rpc.klay.getTransactionReceipt(
                                    txResponse.transactionHash,
                                );
                                if (receipt?.status === '0x1') {
                                    console.log('!!!!! Transaction success:', receipt);
                                    return resolve({
                                        jsonrpc: '2.0',
                                        id: 0,
                                        result: txResponse.transactionHash,
                                    });
                                } else {
                                    console.error('!!!!! Transaction failure:', receipt);
                                    return reject(new Error('Transaction failed.'));
                                }
                            } catch (e) {
                                console.log('### sendRawTransaction error ### : ', e);
                                return reject(new Error('Transaction failed.'));
                            }
                        } else {
                            return reject(new Error('PIN is mismatched.'));
                        }
                    } else {
                        return reject(new Error('PIN is required.'));
                    }
                },
                undefined,
                'Confirm',
            );
        });
    }

    // Kaia Fee Delegated Transaction
    private async _eth_sendTransaction_delegated(params: any[]): Promise<JSONRPCResponse> {
        // send token transaction & send klay transaction
        return new Promise<JSONRPCResponse>(async (resolve, reject) => {
            console.log('!! _eth_sendTransaction params : ', params);
            QRCodeModal.open(
                'DEFAULT',
                async (pin: string) => {
                    console.log('### pin : ', pin);
                    if (pin !== '') {
                        const pinCode = JSON.parse((secureLocalStorage.getItem('pinCode') as string) || '{}');
                        if (pinCode.pincode === pin) {
                            let txParams = params[0];
                            // const { chainId, nonce, to, value, type, data, from, gasLimit, gas } = txParams;
                            const { to, value, data, from, gasPrice, gas } = txParams;
                            console.log('txParams:::', txParams);

                            const gasData: Eip1559Result = await getGasData(
                                this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                                'EIP1559',
                            );
                            console.log('### gasData : ', this.chainId, gasData);

                            const nonce = await getNetworkNonce(this.chainId, from);
                            const feePayer = process.env.REACT_APP_FEEPAYER_SID;

                            // TODO : gasLimit 부분은 Mainnet에서 다시 확인 필요함.
                            // const estimated = await estimate(
                            //     {
                            //         network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                            //         from,
                            //         to: to ?? '0x',
                            //         value: value,
                            //         data: data,
                            //     },
                            //     'EIP1559',
                            // );
                            // const gasLimit = calculateGasMargin(estimated);
                            const gasLimit = calculateGasMargin(gas);
                            console.log('### estimate result : ', gas, gasLimit);

                            let txType = '';
                            if (to) {
                                if (data) txType = 'contractCall';
                                else if (value) txType = 'transfer';
                            } else txType = 'contractDeploy';
                            console.log('!! txType = ', to, value, data.length, txType);

                            // Fee Delegation Transaction
                            const tx = {
                                network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                                txType,
                                feePayer,
                                gasLimit: gasLimit ?? '3000000', // <- 이 값은 어떻게 조절하지 ?
                                gasPrice: ethers.utils
                                    .parseUnits(gasData.high.suggestedMaxFeePerGas, 'gwei')
                                    .toString(),
                                from,
                                to: to ?? '0x',
                                value: value ?? '0x',
                                data: data ?? '0x',
                                nonce,
                            };

                            // Legacy 대납 Raw Transaction 생성
                            let serializedTx = '';
                            try {
                                const senderSignatureHex = await getSenderSignatureHex(tx);
                                await sleep(3);
                                const feePayerSignatureHex = await getFeePayerSignatureHex(tx);
                                console.log('!!!', { senderSignatureHex, feePayerSignatureHex });

                                if (
                                    (!senderSignatureHex && senderSignatureHex !== '') ||
                                    (!feePayerSignatureHex && feePayerSignatureHex !== '')
                                )
                                    return reject(new Error('Transaction failed.'));

                                serializedTx = await delegateRaw({
                                    network: this.chainId === '0x2019' ? 'kaia' : 'kaia_kairos',
                                    txType,
                                    feePayer,
                                    gasLimit: gasLimit ?? '3000000',
                                    gasPrice: ethers.utils
                                        .parseUnits(gasData.high.suggestedMaxFeePerGas, 'gwei')
                                        .toString(),
                                    nonce,
                                    from,
                                    to: to ?? '0x',
                                    value: value ?? '0x',
                                    data: data ?? '0x',
                                    feePayerSignatureHex,
                                    senderSignatureHex,
                                });
                            } catch (e: any) {
                                console.log('Legacy 대납 Raw Transaction 생성 :', e?.message);
                            }

                            // Broadcast
                            try {
                                const txResponse = await this.caver.rpc.klay.sendRawTransaction(serializedTx);
                                console.log('!! txHash = ', txResponse.transactionHash);
                                const receipt = await this.caver.rpc.klay.getTransactionReceipt(
                                    txResponse.transactionHash,
                                );
                                if (receipt?.status === '0x1') {
                                    console.log('!!!!! Transaction success:', receipt);
                                    return resolve({
                                        jsonrpc: '2.0',
                                        id: 0,
                                        result: txResponse.transactionHash,
                                    });
                                } else {
                                    console.error('!!!!! Transaction failure:', receipt);
                                    return reject(new Error('Transaction failed.'));
                                }
                            } catch (e) {
                                console.log('### sendRawTransaction error ### : ', e);
                                return reject(new Error('Transaction failed.'));
                            }
                        } else {
                            return reject(new Error('PIN is mismatched.'));
                        }
                    } else {
                        return reject(new Error('PIN is required.'));
                    }
                },
                undefined,
                'Confirm',
            );
        });
    }

    private async _eth_call(params: unknown[]): Promise<JSONRPCResponse> {
        console.log('_eth_call params ', params);
        if (this.caver == undefined || !this.caverEnabled) {
            throw new Error(ErrorMsgCaverUndefined);
        }
        const result = await this.caver.rpc.klay.call(params[0], params[1]);
        return { jsonrpc: '2.0', id: 0, result };
    }

    private async _eth_getBalance(params: unknown[]): Promise<JSONRPCResponse> {
        console.log('_eth_getBalance params ', params);
        // if (this.caver == undefined || !this.caverEnabled) {
        //     throw new Error(ErrorMsgCaverUndefined);
        // }
        if (this.provider == undefined || !this.providerEnabled) {
            throw new Error(ErrorMsgProviderUndefined);
        }
        console.log('!! _eth_getBalance : ', params[0]);
        // const result = await this.caver.rpc.klay.getBalance(params[0]);
        const result = await this.provider.getBalance(params[0]);
        console.log('!! _eth_getBalance : ', result);
        return { jsonrpc: '2.0', id: 0, result };
    }

    private async _personal_ecRecover(params: unknown[]): Promise<JSONRPCResponse> {
        console.log('_personal_ecRecover params ', params);
        if (this.caver == undefined || !this.caverEnabled) {
            throw new Error(ErrorMsgCaverUndefined);
        }
        const address = await this.caver.utils.recover(params[0], params[1]);
        return { jsonrpc: '2.0', id: 0, result: address };
    }

    private async _eth_getTransactionReceipt(params: unknown[]): Promise<JSONRPCResponse> {
        console.log('!! _eth_getTransactionReceipt params : ', params);
        // if (this.caver == undefined || !this.caverEnabled) {
        //     throw new Error(ErrorMsgCaverUndefined);
        // }
        // const receipt = await this.caver.rpc.klay.getTransactionReceipt(params[0]);

        if (this.provider == undefined || !this.providerEnabled) {
            throw new Error(ErrorMsgProviderUndefined);
        }
        const receipt = await this.provider.getTransactionReceipt(params[0]);
        return { jsonrpc: '2.0', id: 0, result: receipt };
    }

    private async _eth_getTransactionByHash(params: unknown[]): Promise<JSONRPCResponse> {
        console.log('!! _eth_getTransactionByHash params : ', params);
        if (this.provider == undefined || !this.providerEnabled) {
            throw new Error(ErrorMsgProviderUndefined);
        }
        const tx = await this.provider.getTransaction(params[0]);
        console.log('!! _eth_getTransactionByHash : ', tx);
        return { jsonrpc: '2.0', id: 0, result: tx };
    }

    private async _eth_blockNumber(): Promise<JSONRPCResponse> {
        if (this.caver == undefined || !this.caverEnabled) {
            throw new Error(ErrorMsgCaverUndefined);
        }
        const blockNumber = await this.caver.rpc.klay.getBlockNumber();
        return { jsonrpc: '2.0', id: 0, result: blockNumber };
    }

    // TODO: Fuck you Kaia caver-js !!!
    // private async _eth_getBlockByNumber(params: unknown[]): Promise<JSONRPCResponse> {
    //     console.log('!! _eth_getBlockByNumber params : ', params);
    //     if (this.caver == undefined || !this.caverEnabled) {
    //         throw new Error(ErrorMsgCaverUndefined);
    //     }
    //     const block = await this.caver.rpc.klay.getBlockByNumber(params[0], params[1]);
    //     console.log('!! BLOCK : ', block);
    //     return { jsonrpc: '2.0', id: 0, result: block };
    // }
    private async _eth_getBlockByNumber(params: unknown[]): Promise<JSONRPCResponse> {
        if (this.provider == undefined || !this.providerEnabled) {
            throw new Error(ErrorMsgProviderUndefined);
        }

        // const block = await this.provider.rpc.klay.getBlockByNumber(params[0], params[1]);

        let number;
        if (params[0] === 'latest') number = await this.provider.getBlockNumber();
        else number = params[0];

        let block;
        if (params[1]) block = await this.provider.getBlockWithTransactions(number);
        else block = await this.provider.getBlock(number);

        console.log('!! _eth_getBlockByNumber : ', block);
        return { jsonrpc: '2.0', id: 0, result: block };
    }

    private async _eth_getGasPrice(): Promise<JSONRPCResponse> {
        if (this.caver == undefined || !this.caverEnabled) {
            throw new Error(ErrorMsgCaverUndefined);
        }
        const result = await this.caver.rpc.klay.getGasPrice();
        return { jsonrpc: '2.0', id: 0, result: result };
    }
}
